import { CHURNKEY_APP_ID, IS_DEVELOPMENT_ENV } from "constants/config";
import {
  isSubscriptionPausedAndNotPremium,
  UserSubscription,
} from "helpers/subscription";

declare global {
  interface ChurnkeyConfig {
    customerId: string;
    subscriptionId?: string | undefined;
    authHash: string;
    appId: string;
    mode: "live" | "test";
    provider: "stripe" | "chargebee" | "braintree" | "paddle";
    record: boolean;
    onClose: (sessionResults: { aborted: boolean }) => void;
  }
  interface Window {
    churnkey: {
      created?: boolean;
      init: (action: string, config: ChurnkeyConfig) => void;
    };
  }
}

const getChurnkeyConfig = (
  customerId: string,
  subscriptionId: string,
  userHash: string,
  onClose: (sessionResults: { aborted: boolean }) => void
) => ({
  customerId,
  subscriptionId,
  authHash: userHash,
  appId: CHURNKEY_APP_ID!,
  mode: (IS_DEVELOPMENT_ENV ? "test" : "live") as "test" | "live",
  provider: "stripe" as const,
  record: true,
  onClose,
});

const initChurnkey = () => {
  if (!window.churnkey || !window.churnkey.created) {
    window.churnkey = {
      created: true,
      init: () => {},
    };
    const script = document.createElement("script");
    script.src = `https://assets.churnkey.co/js/app.js?appId=${CHURNKEY_APP_ID}`;
    script.async = true;
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode?.insertBefore(script, firstScript);
  }
};

const getUserHash = async (customerId: string) => {
  try {
    const res = await fetch("/api/churnkey", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        customerId,
      }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error("Failed to fetch user_hash:", res.status, errorText);
      throw new Error(`Failed to fetch user_hash: ${res.status} ${errorText}`);
    }

    const data = await res.json();
    return data.userHash;
  } catch (error) {
    throw error;
  }
};

const cancelSubscription = async (
  customerId: string,
  subscriptionId: string
) => {
  try {
    const userHash = await getUserHash(customerId);

    window.churnkey.init(
      "show",
      getChurnkeyConfig(
        customerId,
        subscriptionId,
        userHash,
        (sessionResults: { aborted: boolean }) => {
          // if the session is not aborted, it means the user has cancelled their subscription
          if (!sessionResults.aborted) {
            window.location.reload();
          }
        }
      )
    );
  } catch (error) {
    console.error("Error in cancelSubscription:", error);
    throw error;
  }
};

const unPauseSubscription = async (subscription: UserSubscription) => {
  try {
    const customerId = subscription.customer;
    const subscriptionId = subscription.id;
    if (isSubscriptionPausedAndNotPremium(subscription)) {
      // Show the churnkey paywall if the subscription is paused and premium has expired
      const userHash = await getUserHash(customerId);

      window.churnkey.init(
        "show",
        getChurnkeyConfig(
          customerId,
          subscriptionId,
          userHash,
          (sessionResults: { aborted: boolean }) => {
            // if the session is not aborted, it means the user has resumed their subscription
            if (!sessionResults.aborted) {
              window.location.reload();
            }
          }
        )
      );
    } else {
      // Call the API endpoint to unpause the subscription if is paused and premium is still active
      const response = await fetch(
        "/api/subscription?action=unpauseSubscription",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subscriptionId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Failed to unpause subscription:",
          response.status,
          errorText
        );
        throw new Error(
          `Failed to unpause subscription: ${response.status} ${errorText}`
        );
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to unpause subscription");
      }

      // Reload the page to show the updated subscription status
      window.location.reload();
    }
  } catch (error) {
    console.error("Error in unPauseSubscription:", error);
    throw error;
  }
};

export { cancelSubscription, initChurnkey, unPauseSubscription };
